import {
  ActivityType,
  Gym,
  ILeaderboardConfig,
} from 'fitbeat.models';
import React, { CSSProperties } from 'react';
import MultiSelect from 'react-multi-select-component';
import LeaderboardConfigAdvancedOptions from '../LeaderboardConfigAdvancedOptions';
import LeaderboardConfigBasicOptions from '../LeaderboardConfigBasicOptions';
import StringListEditor from '../formHelpers/StringListEditor';
import { isExperiencePointsMetric } from '../helpers';

export interface ILeaderboardSettingsTabProps {
  formData: ILeaderboardConfig;
  orderedGyms: Gym[];
  timezones: string[];
  isOrgAdmin: boolean;
  allGyms: boolean;
  isUpdate: boolean;
  prizesAndNotes: string[];
  newPrizeOrNote: string;
  groupLinkError: string;
  selectedActivities: ActivityType[];
  activityTypeOptions: { label: string; value: ActivityType }[];
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void;
  handleSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePrizeOrNoteChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddPrizeOrNote: () => void;
  handleDeletePrizeOrNote: (index: number) => void;
  onActivityTypesChange: (selected: { value: ActivityType; label: string }[]) => void;
}

const LeaderboardSettingsTab: React.FC<ILeaderboardSettingsTabProps> = ({
  formData,
  orderedGyms,
  timezones,
  isOrgAdmin,
  allGyms,
  isUpdate,
  prizesAndNotes,
  newPrizeOrNote,
  groupLinkError,
  selectedActivities,
  activityTypeOptions,
  handleInputChange,
  handleSelectChange,
  handleCheckboxChange,
  handlePrizeOrNoteChange,
  handleAddPrizeOrNote,
  handleDeletePrizeOrNote,
  onActivityTypesChange,
}) => {
  const selectedOptions = activityTypeOptions.filter((option) => selectedActivities.includes(option.value));
  const hasActivityRestriction = selectedActivities.length > 0;
  const isBroken = hasActivityRestriction && isExperiencePointsMetric({ metric: formData.metric });
  const activityRestrictionDisabled = isExperiencePointsMetric({ metric: formData.metric }) || isBroken;

  return (
    <div style={styles.formColumn}>
      {isBroken && (
        <div style={styles.brokenBanner}>
          This leaderboard is misconfigured: experience points cannot be used with activity type restrictions.
          This leaderboard is disabled.
        </div>
      )}
      <div style={styles.formRow}>
        <LeaderboardConfigBasicOptions
          formData={formData}
          orderedGyms={orderedGyms}
          timezones={timezones}
          isOrgAdmin={isOrgAdmin}
          allGyms={allGyms}
          isUpdate={isUpdate}
          hasActivityRestriction={hasActivityRestriction}
          isBroken={isBroken}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
        />
        <div style={styles.listInputCol}>
          <StringListEditor
            title='Content'
            subHeader='Prizes and notes'
            items={prizesAndNotes}
            newItem={newPrizeOrNote}
            onNewItemChange={handlePrizeOrNoteChange}
            onAddItem={handleAddPrizeOrNote}
            onDeleteItem={handleDeletePrizeOrNote}
            placeholder='Enter a prize or note here'
            showDeleteForOriginalItem
            disabled={isBroken}
          />
          <label style={styles.inputLabel}>
            Restrict workouts to these activity types:
            <MultiSelect
              options={activityTypeOptions}
              value={selectedOptions}
              disabled={activityRestrictionDisabled}
              onChange={(selected: [{ value: ActivityType; label: string }]) => {
                onActivityTypesChange(selected);
              }}
              valueRenderer={(selected) => {
                if (selected.length === 0
                || selected.length === activityTypeOptions.length
                ) {
                  return 'All activities allowed';
                }
                if (selected.length === 1) {
                  return `${selected[0].label} allowed`;
                }
                return `${selected.length} allowed`;
              }}
              labelledBy='Select activities'
            />
            {activityRestrictionDisabled && (
            <div style={styles.disabledHint}>
              Not available for experience points leaderboards
            </div>
            )}
          </label>
          <label style={styles.inputLabel}>
            Social media group link:
            <input
              type='text'
              name='groupLink'
              value={formData.groupLink}
              onChange={handleInputChange}
              style={styles.inputField}
              disabled={isBroken}
            />
            {
            groupLinkError && (
              <div style={{ color: 'red', fontSize: '0.85em', marginTop: '4px' }}>
                {groupLinkError}
              </div>
            )
          }
          </label>
          <label style={styles.inputLabel}>
            Header Image URL (optional):
            <input
              type='url'
              name='headerImageUrl'
              value={formData.headerImageUrl ?? ''}
              onChange={handleInputChange}
              placeholder='https://'
              style={styles.inputField}
              disabled={isBroken}
            />
          </label>
          <label style={styles.inputLabel}>
            Source Group Template ID:
            <input
              type='text'
              value={formData.sourceGroupTemplateId ?? ''}
              disabled
              style={styles.inputField}
            />
          </label>
        </div>
        <LeaderboardConfigAdvancedOptions
          formData={formData}
          handleCheckboxChange={handleCheckboxChange}
          isBroken={isBroken}
        />
      </div>
    </div>
  );
};

const styles: { [key: string]: CSSProperties } = {
  formColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  brokenBanner: {
    backgroundColor: '#fdecea',
    border: '1px solid #f5c6cb',
    color: '#a71d2a',
    padding: '10px 12px',
    borderRadius: '4px',
    fontWeight: 'bold',
  },
  formRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: '6px',
    gap: '20px',
  },
  listInputCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputLabel: {
    display: 'flex',
    fontWeight: 'bold',
    flexDirection: 'column',
  },
  inputField: {
    padding: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    marginTop: '4px',
  },
  disabledHint: {
    color: '#888',
    fontSize: '0.85em',
    fontWeight: 'normal',
    marginTop: '4px',
  },
};

export default LeaderboardSettingsTab;
